import { useField } from 'formik';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

export const fieldHelpersPropType = PropTypes.objectOf(
    PropTypes.shape({
        setValue: PropTypes.func,
    })
);

/**
 * HOC to inject multiple Formik field values into a component. Memoizes the wrapped component to reduce unnecessary rerenders.
 * @param {React.ComponentType} WrappedComponent - The component to wrap
 * @param {(string|function)[]} fieldNames - Array of Formik field name strings or functions that receive the wrapped component's props and return a field name string
 */
function withFormikFields({ WrappedComponent, fieldNames }) {
    const MemoizedWrappedComponent = React.memo(WrappedComponent, isEqual);
    const ComponentWithFields = (props) => {
        const fieldValues = {};
        const fieldHelpers = {};
        fieldNames.forEach((fieldSpec) => {
            const resolvedFieldName = typeof fieldSpec === 'function' ? fieldSpec(props) : fieldSpec;
            // eslint-disable-next-line react-hooks/rules-of-hooks -- linter is mistaken
            const [field, _, helpers] = useField(resolvedFieldName);
            fieldValues[resolvedFieldName] = field.value;
            fieldHelpers[resolvedFieldName] = helpers;
        });
        return <MemoizedWrappedComponent {...props} fieldValues={fieldValues} fieldHelpers={fieldHelpers} />;
    };
    ComponentWithFields.displayName = `withFormikFields(${WrappedComponent.displayName || WrappedComponent.name})`;
    return ComponentWithFields;
}

export default withFormikFields;
